"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { PlatformError } from "@/lib/api/client";
import { FIAT_METHOD_GONE, fiatDepositErrorMessage, withdrawErrorMessages } from "@/lib/api/messages";
import { isKycRequiredError } from "@/lib/api/kyc";
import { useHandleKycRequired } from "@/lib/kyc/hooks";
import { KycVerifyIdentityBanner } from "@/components/kyc/KycVerifyIdentityBanner";
import {
  parseFiatAmount,
  useInitiateFiatDeposit,
  useInitiateFiatWithdrawal,
  type FiatMethod,
  type RouteOption,
} from "@/lib/api/transfers";
import { toBaseUnits } from "@/lib/format/amount";
import { amountError, hasInvalidAmountCharacters, sanitizeAmountInput } from "@/lib/wallet/amount";
import { Icon } from "@/components/ui/Icon";
import { Button } from "@/components/ui/primitives";
import { useEmailVerificationRedirect } from "./email-gate";
import { Field, Note } from "./fields";
import { PaymentMarks, StablemintMark, StablemintWordmark } from "./brand";
import { StablemintCardDepositModal } from "./StablemintCardDepositModal";

/* Card and bank deposits, and bank withdrawals, on Stablemint's hosted pages.

   Modelled on the kit's `modal-deposit-card` / `modal-deposit-bank` frames: an
   amount, the provider's own CTA — card marks over a "Debit or Credit Card"
   button, or the Stablemint mark on an "Instant Bank Transfer" one — and a
   "Powered by Stablemint" line. Nothing sensitive is collected here: the
   platform opens a session against the route and answers with a URL, and the
   player finishes on Stablemint — a card payment in a frame over this page, a
   bank transfer on Stablemint's own site, which returns to `/fiat/…`.

   The method (card or bank) is fixed by the rail pill the player picked, not a
   toggle inside the panel, so this renders exactly one flow. `amount` crosses
   the wire as a plain number in the balance's own currency — the opposite of
   the crypto path's base units — which is what parseFiatAmount produces. */

export function FiatPanel({
  option,
  direction,
  method,
  balance,
}: {
  option: RouteOption;
  direction: "deposit" | "withdraw";
  method: FiatMethod;
  /* The bankroll balance a withdrawal is taken from, base units. */
  balance?: string;
}) {
  const [amount, setAmount] = useState("");
  const [failure, setFailure] = useState<string[] | null>(null);
  const [cardUrl, setCardUrl] = useState<string | null>(null);

  const client = useQueryClient();
  const deposit = useInitiateFiatDeposit();
  const withdrawal = useInitiateFiatWithdrawal();
  const handleKycRequired = useHandleKycRequired();
  const redirectToEmailVerification = useEmailVerificationRedirect();
  const closeCard = useCallback(() => setCardUrl(null), []);

  const pending = deposit.isPending || withdrawal.isPending;
  const parsed = parseFiatAmount(amount);
  const scale = option.bankroll.token;

  /* A withdrawal is held to the balance it comes out of, at the bankroll
     token's scale, as the whitelabel's FiatWithdrawContent validates it. */
  const invalid =
    amount.trim() === ""
      ? null
      : direction === "withdraw"
        ? amountError(amount, {
            minAmount: 0n,
            maxAmount: toBaseUnits(balance),
            decimals: scale.decimals,
            significantDecimals: scale.significantDecimals,
            actionLabel: "withdrawal",
          })
        : parsed === null
          ? "Please enter a valid amount"
          : null;

  const start = async () => {
    if (parsed === null || invalid !== null) {
      return;
    }

    setFailure(null);

    try {
      if (direction === "deposit") {
        const session = await deposit.mutateAsync({ routeId: option.route.id, amount: parsed, method });

        setAmount("");

        if (method === "card") {
          setCardUrl(session.url);
          return;
        }

        /* Short-lived, single-use — hand over now rather than behind a link. */
        window.location.assign(session.url);
        return;
      }

      const session = await withdrawal.mutateAsync({ routeId: option.route.id, amount: parsed });

      setAmount("");
      window.location.assign(session.url);
    } catch (cause) {
      const error = cause instanceof PlatformError ? cause : new PlatformError(0, "network", "");

      /* The route is gone since the bankrolls were read: say so and read
         them again, so the rail disappears if it no longer exists. */
      if (error.status === 404) {
        void client.invalidateQueries({ queryKey: ["bankrolls"] });
        setFailure([FIAT_METHOD_GONE]);
        return;
      }

      if (direction === "deposit") {
        setFailure([fiatDepositErrorMessage(error, method)]);
        return;
      }

      if (isKycRequiredError(error)) {
        handleKycRequired();
        return;
      }

      if (error.message === "EMAIL_VERIFICATION_REQUIRED") {
        redirectToEmailVerification(withdrawErrorMessages(error, scale).join(" "));
        return;
      }

      setFailure(withdrawErrorMessages(error, scale));
    }
  };

  const isCard = method === "card" && direction === "deposit";

  return (
    <div className="flex w-full flex-col gap-[16px]">
      {direction === "withdraw" && <KycVerifyIdentityBanner />}
      <Field
        id={`fiat-${direction}-${method}-amount`}
        label={`Amount in ${option.token.symbol}`}
        value={amount}
        onChange={(value) => setAmount(hasInvalidAmountCharacters(value) ? sanitizeAmountInput(value) : value)}
        placeholder="Enter Amount"
        inputMode="decimal"
        numeric
        error={invalid}
      />

      <div className="flex flex-col items-center gap-[16px]">
        {isCard && <PaymentMarks />}

        <Button
          onClick={start}
          disabled={pending || parsed === null || invalid !== null}
          className="flex w-full items-center justify-center gap-[8px]"
        >
          {pending ? (
            "Processing..."
          ) : isCard ? (
            <>
              <Icon name="card" size={20} />
              Debit or Credit Card
            </>
          ) : (
            <>
              {/* The kit's dark badge behind the lime mark. Inline-styled, not
                  arbitrary utility classes — one of those silently missed the
                  stylesheet once, and a style attribute cannot. */}
              <span
                className="grid place-items-center"
                style={{ width: 24, height: 24, borderRadius: 8, background: "#122424" }}
              >
                <StablemintMark />
              </span>
              Instant Bank Transfer
            </>
          )}
        </Button>

        <span className="flex items-center gap-[8px] text-[11px] leading-[1.5] text-fg-muted">
          Powered by
          <StablemintWordmark />
        </span>
      </div>

      {failure && (
        <Note tone="error">
          {failure.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </Note>
      )}

      {cardUrl && <StablemintCardDepositModal url={cardUrl} onClose={closeCard} />}
    </div>
  );
}
